// Projects, rate cards and deployment billing (UI design §3.7 — the CFO's month-end screen). Money is readable by
// managers and super admins only (RLS on deployments / rate_cards); day and rate changes go through the idempotent
// adjust_deployment_days / set_deployment_terms RPCs, which keep an audit line per change.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

export type ProjectStatus = 'planned' | 'active' | 'completed' | 'cancelled';
export const PROJECT_STATUSES: ProjectStatus[] = ['planned', 'active', 'completed', 'cancelled'];

export interface Project {
  id: string;
  name: string;
  client_name: string | null;
  start_date: string | null;
  end_date: string | null;
  status: ProjectStatus;
  note: string | null;
  sites: { id: string; name: string; type: string }[];
}

/** An audit line on a deployment: a day adjustment ({days}) or a rate change ({rate_from, rate_to}). */
export interface Adjustment {
  days?: number;
  rate_from?: number | null;
  rate_to?: number | null;
  reason?: string;
  by?: string;
  at?: string;
}

export interface BillingRow {
  deployment_id: string;
  project_id: string;
  project_name: string;
  client_name: string | null;
  asset_id: string;
  asset_code: string;
  asset_description: string | null;
  category_name: string | null;
  site_id: string | null;
  site_name: string | null;
  started_at: string;
  ended_at: string | null;
  expected_return_at: string | null;
  period_days: number;
  adjustment_days: number;
  days_billable: number;
  rate: number | null;
  amount: number;
  currency: string | null;
  adjustments: Adjustment[];
}

export interface RateCard {
  id: string;
  category_id: string | null;
  project_id: string | null;
  day_rate: number;
  currency: string;
  asset_categories: { name: string } | null;
  projects: { name: string } | null;
}

async function data<T>(
  q: PromiseLike<{ data: unknown; error: { message: string; code?: string } | null }>,
): Promise<T> {
  const { data: d, error } = await q;
  if (error) throw Object.assign(new Error(error.message), { code: error.code });
  return d as T;
}

export const billingKeys = {
  projects: ['projects'] as const,
  project: (id: string) => ['projects', id] as const,
  billing: (projectId: string | null, from: string, to: string) =>
    ['billing', projectId, from, to] as const,
  rates: ['rate-cards'] as const,
};

const PROJECT_COLUMNS =
  'id, name, client_name, start_date, end_date, status, note, sites(id, name, type)';

export function useProjects() {
  return useQuery({
    queryKey: billingKeys.projects,
    queryFn: () =>
      data<Project[]>(supabase().from('projects').select(PROJECT_COLUMNS).order('name')),
  });
}

/** One project, or null when it doesn't exist or RLS hides it (the page then says "not found"). */
export function useProject(id: string) {
  return useQuery({
    queryKey: billingKeys.project(id),
    queryFn: () =>
      data<Project | null>(
        supabase().from('projects').select(PROJECT_COLUMNS).eq('id', id).maybeSingle(),
      ),
  });
}

/** Deployments with the days and amount inside [from, to] (company-timezone dates, inclusive). */
export function useBilling(projectId: string | null, from: string, to: string, enabled = true) {
  return useQuery({
    queryKey: billingKeys.billing(projectId, from, to),
    queryFn: () =>
      data<BillingRow[]>(
        supabase().rpc('deployment_billing_period', {
          p_from: from,
          p_to: to,
          p_project: projectId,
        }),
      ),
    enabled: enabled && !!from && !!to && from <= to,
    // deployments open and close server-side (presence engine, receipts): never show a cached period on arrival
    staleTime: 0,
  });
}

export function useSaveProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id?: string;
      companyId: string;
      name: string;
      client_name: string;
      start_date: string;
      end_date: string;
      status: ProjectStatus;
      note: string;
    }) => {
      const row = {
        name: input.name.trim(),
        client_name: input.client_name.trim() || null,
        start_date: input.start_date || null,
        end_date: input.end_date || null,
        status: input.status,
        note: input.note.trim() || null,
      };
      const [saved] = await data<{ id: string }[]>(
        input.id
          ? supabase().from('projects').update(row).eq('id', input.id).select('id')
          : supabase()
              .from('projects')
              .insert({ ...row, company_id: input.companyId })
              .select('id'),
      );
      if (!saved) throw new Error('project not saved');
      // locations are linked inside the project (the link lives on the site: sites.project_id)
      return saved.id;
    },
    onSuccess: () => {
      for (const key of [billingKeys.projects, ['sites'], ['site-rows']])
        void queryClient.invalidateQueries({ queryKey: key });
    },
  });
}

function useBillingCommand<A>(fn: (args: A) => Promise<unknown>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['billing'] }),
  });
}

const rpc = (fn: string, args: Record<string, unknown>) =>
  data(supabase().rpc(fn, { p_idempotency_key: crypto.randomUUID(), ...args }));

export const useAdjustDays = () =>
  useBillingCommand((a: { deploymentId: string; days: number; reason: string }) =>
    rpc('adjust_deployment_days', {
      p_deployment_id: a.deploymentId,
      p_days: a.days,
      p_reason: a.reason,
    }),
  );

export const useSetTerms = () =>
  useBillingCommand(
    (a: {
      deploymentId: string;
      rate: number | null;
      expectedReturnAt: string | null;
      reason: string;
    }) =>
      rpc('set_deployment_terms', {
        p_deployment_id: a.deploymentId,
        p_rate: a.rate,
        p_expected_return_at: a.expectedReturnAt,
        p_reason: a.reason || null,
      }),
  );

export function useRateCards() {
  return useQuery({
    queryKey: billingKeys.rates,
    queryFn: () =>
      data<RateCard[]>(
        supabase()
          .from('rate_cards')
          .select(
            'id, category_id, project_id, day_rate, currency, asset_categories(name), projects(name)',
          )
          .order('created_at'),
      ),
  });
}

export function useSaveRateCard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id?: string;
      companyId: string;
      category_id: string | null;
      project_id: string | null;
      day_rate: number;
      currency: string;
    }) => {
      const row = {
        category_id: input.category_id,
        project_id: input.project_id,
        day_rate: input.day_rate,
        currency: input.currency.trim().toUpperCase(),
      };
      return data(
        input.id
          ? supabase().from('rate_cards').update(row).eq('id', input.id)
          : supabase()
              .from('rate_cards')
              .insert({ ...row, company_id: input.companyId }),
      );
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: billingKeys.rates }),
  });
}

export function useDeleteRateCard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => data(supabase().from('rate_cards').delete().eq('id', id)),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: billingKeys.rates }),
  });
}

/** Names for the "by" of audit lines (managers read their company's profiles). */
export function useProfileNames(ids: string[]) {
  const unique = [...new Set(ids)].sort();
  return useQuery({
    queryKey: ['profile-names', unique],
    queryFn: async () => {
      const rows = await data<{ user_id: string; full_name: string | null }[]>(
        supabase().from('profiles').select('user_id, full_name').in('user_id', unique),
      );
      return new Map(rows.map((r) => [r.user_id, r.full_name ?? '']));
    },
    enabled: unique.length > 0,
  });
}

/** Totals per currency (a project normally has one; rows without a rate card have none). */
export function totals(rows: BillingRow[]): { currency: string; days: number; amount: number }[] {
  const by = new Map<string, { currency: string; days: number; amount: number }>();
  for (const r of rows) {
    const key = r.currency ?? '';
    const t = by.get(key) ?? { currency: key, days: 0, amount: 0 };
    t.days += r.days_billable;
    t.amount = Math.round((t.amount + Number(r.amount)) * 100) / 100;
    by.set(key, t);
  }
  return [...by.values()];
}
